<template>
	<CDialog
		:visible="show"
		:custom-class="`upsell-dialog${featureData.videoId ? ' upsell-dialog--video' : ''}`"
		:destroy-on-close="!!featureData.videoId"
		:data-testid="`upsell-dialog-${featureData.featureCode}`"
		@closed="$emit('closed')"
		@close="$emit('close')"
		@done="$emit('close')"
	>
		<div v-if="featureData.videoId" class="upsell-dialog__layout grid">
			<div class="upsell-dialog__video">
				<iframe
					v-if="show"
					:src="`https://www.youtube-nocookie.com/embed/${featureData.videoId}?autoplay=1&mute=1&rel=0&controls=0`"
					title="Feature preview video"
					allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
					allowfullscreen
					frameborder="0"
				/>
			</div>
			<div class="upsell-dialog__header flex flex-col gap-3 mb-0">
				<Inline
					v-if="featureData.illustration"
					:svg="`complex-icons/${featureData.illustration}`"
					class="upsell-dialog__icon b3:block b9:hidden b12:block mb-3"
					:class="`stroke-${featureData.illustrationStroke || 'shade-10'} fill-${featureData.illustrationFill || 'accent-4'}`"
				/>
				<h2 class="text-accent-7 text-3 uppercase">{{ featureData.plan ? `${featureData.plan} Feature` : `Upgrade for more powerful&nbsp;forms` }}</h2>
				<h1 class="upsell-dialog__title heavy mb-0" :class="featureData.shortTitle ? 'text-7 b9:text-6 b12:text-7' : 'text-7'">{{ featureData.title }}</h1>
				<!-- eslint-disable-next-line vue/no-v-html -->
				<div v-if="featureData.rawHtml" class="upsell-dialog__description sz-3 text-balance b9:text-pretty" v-html="featureData.description"></div>
				<p v-else class="upsell-dialog__description sz-3 text-balance b9:text-pretty mb-0">{{ featureData.description }}</p>
				<div v-if="isOrgOwner" class="upsell-dialog__buttons flex gap-4 pt-0 b9:show">
					<a class="button button--secondary sz-4 nowrap" :href="planPageHref" target="_blank" rel="noopener noreferrer">Compare Plans</a>

					<a class="button button--primary bg-cta-intense sz-4 nowrap" :href="upgradeHref" v-bind="upgradeAttrs" target="_blank" rel="noopener noreferrer">
						<span>Upgrade Now</span>
					</a>
				</div>
			</div>
			<div v-if="isOrgOwner" class="upsell-dialog__buttons flex gap-4 pt-0 b9:hide">
				<a class="button button--secondary sz-4 nowrap" :href="planPageHref">Compare Plans</a>

				<a class="button button--primary bg-cta-intense sz-4 nowrap" :href="upgradeHref" v-bind="upgradeAttrs">
					<span>Upgrade Now</span>
				</a>
			</div>
		</div>
		<div v-else class="text-center px-2 pb-3">
			<Inline
				v-if="featureData.illustration"
				:svg="`complex-icons/${featureData.illustration}`"
				class="w-33 mb-3"
				:class="`stroke-${featureData.illustrationStroke || 'shade-10'} fill-${featureData.illustrationFill || 'accent-4'}`"
			/>
			<h2 class="text-accent-7 text-3 mb-1 uppercase">{{ featureData.plan ? `${featureData.plan} Feature` : 'Upgrade for more powerful&nbsp;forms' }}</h2>
			<h1 class="sz-6 b5:sz-7 heavy mb-3">{{ featureData.title }}</h1>
			<div class="flex--align">
				<p v-if="!featureData.rawHtml" class="mb-3 px-3" :class="[featureData.description.length < 85 ? 'sz-4' : 'sz-3', { 'text-balance': featureData.balanceDescription }]">{{ featureData.description }}</p>
				<!-- eslint-disable-next-line vue/no-v-html -->
				<div v-else class="mb-3 px-3" :class="featureData.description.length < 85 ? 'sz-4' : 'sz-3'" v-html="featureData.description"></div>
				<div v-if="isOrgOwner">
					<a class="button--primary bg-cta-intense my-3 sz-4 p-3" :href="upgradeHref" data-testid="upsell-dialog-upgrade" v-bind="upgradeAttrs">{{ buttonText }}</a><br />
					<a :href="planPageHref" target="_blank">or see all features &amp; plans</a>
				</div>
			</div>
		</div>
		<div v-if="!isOrgOwner" data-testid="upsell-dialog-non-owner">
			<CCallout type="warning" class="mb-0">
				<p>This feature requires a higher plan level. Please ask the organization owner to upgrade.</p>
			</CCallout>
		</div>
	</CDialog>
</template>
<script lang="ts">
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import featureMetadata from '../app/featureMetadata.js';
	import type { PropType } from 'vue';
	import { computed, defineComponent } from 'vue';
	import SessionConsumer from 'src/mixins/session-consumer';

	export type UpsellFeature = keyof typeof featureMetadata;

	export default defineComponent({
		name: 'CUpsellDialog',
		components: {
			CDialog,
			CCallout
		},
		mixins: [SessionConsumer],
		props: {
			show: {
				type: Boolean,
				default: false
			},
			feature: {
				type: String as PropType<UpsellFeature>,
				required: true,
				validator(value) {
					return featureMetadata[value];
				}
			},
			orgCode: {
				type: String,
				default: null
			},
			isOrgOwner: {
				type: Boolean,
				default: true
			}
		},
		emits: ['close', 'closed'],
		setup(props) {
			const featureData = computed(() => featureMetadata[props.feature]);
			const buttonText = computed(() => featureData.value.buttonText || 'Upgrade Now');

			return {
				featureData,
				buttonText
			};
		},
		computed: {
			useNewBilling() {
				if (this.session?.value)
					return this.session?.value?.enabledFeatures.NewBillingPipeline;
				return window.Cognito.config.flags.NewBillingPipeline;
			},
			planPageHref() {
				return `/${this.orgCode}/settings/plan/select?source=tryitnow-feature&details=${this.featureData.featureCode}`;
			},
			isDirectCheckout() {
				return Boolean(this.featureData.checkoutPlan) && this.useNewBilling;
			},
			upgradeAttrs() {
				return this.isDirectCheckout ? {} : { target: '_blank' };
			},
			upgradeHref() {
				if (!this.isDirectCheckout)
					return this.planPageHref;

				const params = new URLSearchParams({
					plan: this.featureData.checkoutPlan,
					prepaymonths: '0',
					prepayusers: '0',
					prepaytemplates: '0',
					interval: 'annual',
					source: 'tryitnow-feature',
					details: this.featureData.featureCode
				});
				return `/${this.orgCode}/settings/plan/checkout?${params.toString()}`;
			}
		}
	});
</script>

<style lang="scss">
	@import '@cognitoforms/styles/app/common/resources/app-design-tokens';

	.upsell-dialog {
		max-width: 550px;
		line-height: normal;

		&--video {
			max-width: 1500px;

			@include b-down($b12) {
				max-width: 1200px;
			}

			@include b-down($b9) {
				max-width: 600px;
			}
		}

		&__layout {
			grid-template-areas:
				'header'
				'video';
			gap: var(--spacing-4);

			@include b-up($b9) {
				align-items: center;
				grid-template-areas: 'video header';
				grid-template-columns: 1.5fr 1fr;
				gap: var(--spacing-5);
			}
		}

		&__header {
			align-items: center;

			@include b-up($b9) {
				align-items: normal;
			}
		}

		&__icon {
			width: 64px;

			@include b-down($b9) {
				width: 33%;
			}
		}

		&__title {
			text-align: center;

			@include b-up($b9) {
				text-align: left;
			}
		}

		&__description {
			text-align: left;

			@include b-down($b9) {
				padding-bottom: var(--spacing-3);
				text-align: center;
			}
		}

		&__video {
			position: relative;
			width: 100%;
			border: 1px solid $stroke__input;
			overflow: hidden;
			grid-area: video;
			aspect-ratio: 16 / 9;

			iframe {
				position: absolute;
				inset: -.5%;
				width: 101%;
				height: 101%;
			}

			@include b-down($b9) {
				display: none;
			}
		}

		&__buttons {
			justify-content: flex-end;
			margin-top: 0;

			@include b-down($b9) {
				justify-content: center;
				padding-top: var(--spacing-3);
			}

			@include b-up($b9) {
				margin-top: var(--spacing-4);
			}

			@include b-up($b12) {
				margin-top: var(--spacing-6);
			}
		}

		.el-dialog__header {
			padding-bottom: 0;
		}

		h1,
		h2 {
			font-family: var(--font-display);
			font-weight: $weight__display--bold;
		}

		h1 {
			margin-top: 0;
			line-height: 1.1;
		}

		.button--primary {
			font-family: var(--font-display);

			@include hover(var(--bg-cta-intense-hovered));
		}

		:root & {

			.sz-2 {
				font-size: $size__2;
			}

			.sz-3,
			.sz-4 {
				font-size: $size__3;
			}

			.sz-5,
			.sz-6 {
				font-size: $size__7;
			}
		}
	}

</style>